import React, { useState, useCallback, useMemo } from 'react';
import { 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Button, 
  Select, 
  SelectItem, 
  Textarea, 
  Card, 
  CardBody, 
  CardHeader,
  Spinner, 
  Chip,
  Divider
} from '@heroui/react';
import { 
  CpuChipIcon, 
  ShieldCheckIcon, 
  UserIcon, 
  UsersIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { OSINTIntelligenceService } from '../../services/osintIntelligenceService';

const OSINTAnalysisModal = ({ 
  isOpen, 
  onClose, 
  osintType, 
  osintData, 
  onAnalysisComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [analysisType, setAnalysisType] = useState('general');
  const [investigationContext, setInvestigationContext] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const osintService = useMemo(() => new OSINTIntelligenceService(), []);

  const analysisTypes = [
    {
      key: 'general',
      label: 'General Analysis',
      description: 'Comprehensive OSINT analysis with actionable insights',
      icon: <CpuChipIcon className="h-4 w-4" />,
      color: 'primary'
    },
    {
      key: 'threat',
      label: 'Threat Assessment',
      description: 'Security-focused analysis for threats and vulnerabilities',
      icon: <ShieldCheckIcon className="h-4 w-4" />,
      color: 'danger'
    },
    {
      key: 'person',
      label: 'Person Intelligence',
      description: 'People-focused analysis for individual profiling',
      icon: <UserIcon className="h-4 w-4" />,
      color: 'warning'
    },
    {
      key: 'social',
      label: 'Social Media Intelligence',
      description: 'Social platform analysis and network mapping',
      icon: <UsersIcon className="h-4 w-4" />,
      color: 'secondary'
    }
  ];

  const getRecommendedAnalysisType = useCallback(() => {
    return osintService.getRecommendedAnalysisType(osintType);
  }, [osintService, osintType]);

  const handleAnalyze = async () => {
    if (!osintData) {
      setError('No OSINT data available for analysis');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisResult(null);

    try {
      const options = {
        investigationContext: investigationContext.trim() || undefined,
        analysisType: analysisType,
        targetType: osintType
      };

      const result = await osintService.processExecutedOSINTResults(osintType, osintData, options);

      if (result.success) {
        setAnalysisResult(result);
        if (onAnalysisComplete) {
          onAnalysisComplete(result);
        }
      } else {
        setError(result.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze OSINT data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAnalysis = () => {
    if (analysisResult?.intelligence?.analysis) {
      navigator.clipboard.writeText(analysisResult.intelligence.analysis);
    }
  };

  const handleCopyFullReport = () => {
    if (analysisResult) {
      const fullReport = {
        osintType: osintType,
        analysisType: analysisResult.analysisType,
        timestamp: analysisResult.processingTime,
        investigationContext: investigationContext,
        intelligence: analysisResult.intelligence,
        rawData: osintData
      };
      navigator.clipboard.writeText(JSON.stringify(fullReport, null, 2));
    }
  };

  const resetModal = () => {
    setAnalysisResult(null);
    setError(null);
    setInvestigationContext('');
    setAnalysisType(getRecommendedAnalysisType());
  };

  const handleModalClose = () => {
    resetModal();
    onClose();
  };

  // Initialize analysis type on modal open
  React.useEffect(() => {
    if (isOpen && osintType) {
      setAnalysisType(getRecommendedAnalysisType());
    }
  }, [isOpen, osintType, getRecommendedAnalysisType]);

  const getThreatLevelColor = (level) => {
    switch (level) {
      case 'HIGH': return 'danger';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'HIGH': return 'success';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'danger';
      default: return 'default';
    }
  };

  const formatAnalysisText = (text) => {
    if (!text) return '';
    
    // Convert markdown-style formatting to JSX
    return text
      .split('\n')
      .map((line, index) => {
        // Handle headers
        if (line.startsWith('**') && line.endsWith('**')) {
          return (
            <div key={index} className="font-semibold text-sm text-primary mt-4 mb-2 first:mt-0">
              {line.replace(/\*\*/g, '')}
            </div>
          );
        }
        
        // Handle bold text within lines
        if (line.includes('**')) {
          const parts = line.split('**');
          return (
            <div key={index} className="mb-2">
              {parts.map((part, partIndex) => (
                partIndex % 2 === 1 ? 
                  <strong key={partIndex}>{part}</strong> : 
                  <span key={partIndex}>{part}</span>
              ))}
            </div>
          );
        }
        
        // Handle bullet points
        if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
          return (
            <div key={index} className="mb-1 ml-4">
              <span className="text-primary mr-2">•</span>
              {line.replace(/^[\s\-•]+/, '')}
            </div>
          );
        }
        
        // Handle numbered lists
        if (/^\d+\./.test(line.trim())) {
          return (
            <div key={index} className="mb-1 ml-4">
              {line.trim()}
            </div>
          );
        }
        
        // Regular lines
        return line.trim() ? (
          <div key={index} className="mb-2">
            {line}
          </div>
        ) : (
          <div key={index} className="mb-2"></div>
        );
      });
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleModalClose}
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[90vh]",
        body: "p-0"
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-3 pb-2 border-b">
          <CpuChipIcon className="h-6 w-6 text-primary" />
          <div>
            <h3 className="text-lg font-semibold">AI OSINT Analysis</h3>
            <p className="text-sm text-gray-500 font-normal">
              Intelligent analysis of {osintType.replace(/-/g, ' ')} data
            </p>
          </div>
        </ModalHeader>

        <ModalBody className="p-6">
          <div className="space-y-6">
            {/* Analysis Configuration */}
            {!analysisResult && (
              <>
                <Card>
                  <CardHeader className="pb-2">
                    <h4 className="font-semibold text-primary">Analysis Configuration</h4>
                  </CardHeader>
                  <CardBody className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Analysis Type</label>
                      <Select
                        value={analysisType}
                        onChange={(value) => setAnalysisType(value)}
                        placeholder="Select analysis type"
                        classNames={{
                          trigger: "min-h-unit-12"
                        }}
                      >
                        {analysisTypes.map((type) => (
                          <SelectItem
                            key={type.key}
                            value={type.key}
                            startContent={type.icon}
                            description={type.description}
                          >
                            {type.label}
                          </SelectItem>
                        ))}
                      </Select>
                      <div className="mt-2">
                        <Chip 
                          size="sm" 
                          variant="flat" 
                          color="primary"
                          startContent={<InformationCircleIcon className="h-3 w-3" />}
                        >
                          Recommended: {analysisTypes.find(t => t.key === getRecommendedAnalysisType())?.label}
                        </Chip>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Investigation Context (Optional)
                      </label>
                      <Textarea
                        value={investigationContext}
                        onChange={(e) => setInvestigationContext(e.target.value)}
                        placeholder="Provide context about your investigation (case type, specific goals, etc.)"
                        minRows={3}
                        maxRows={5}
                        classNames={{
                          input: "resize-none"
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Adding context helps the AI provide more targeted and relevant analysis
                      </p>
                    </div>
                  </CardBody>
                </Card>

                {/* Data Preview */}
                <Card>
                  <CardHeader className="pb-2">
                    <h4 className="font-semibold text-gray-700">Data to Analyze</h4>
                  </CardHeader>
                  <CardBody>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 max-h-32 overflow-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {JSON.stringify(osintData, null, 2)}
                      </pre>
                    </div>
                  </CardBody>
                </Card>
              </>
            )}

            {/* Error Display */}
            {error && (
              <Card className="border-danger">
                <CardBody>
                  <div className="flex items-center gap-2 text-danger">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span className="font-medium">Analysis Error</span>
                  </div>
                  <p className="text-sm mt-2">{error}</p>
                </CardBody>
              </Card>
            )}

            {/* Loading State */}
            {loading && (
              <Card>
                <CardBody>
                  <div className="flex items-center justify-center gap-3 py-8">
                    <Spinner size="lg" />
                    <div className="text-center">
                      <p className="font-medium">Analyzing OSINT Data...</p>
                      <p className="text-sm text-gray-500 mt-1">
                        AI is processing {osintType.replace(/-/g, ' ')} intelligence
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Analysis Results */}
            {analysisResult && (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center w-full">
                      <h4 className="font-semibold text-success flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5" />
                        Analysis Complete
                      </h4>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="light"
                          startContent={<ClipboardDocumentIcon className="h-4 w-4" />}
                          onClick={handleCopyAnalysis}
                        >
                          Copy Analysis
                        </Button>
                        <Button
                          size="sm"
                          variant="light"
                          startContent={<ClipboardDocumentIcon className="h-4 w-4" />}
                          onClick={handleCopyFullReport}
                        >
                          Copy Full Report
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="space-y-4">
                    {/* Analysis Metadata */}
                    <div className="flex flex-wrap gap-2">
                      <Chip size="sm" variant="flat" color="primary">
                        {analysisResult.analysisType} Analysis
                      </Chip>
                      <Chip size="sm" variant="flat">
                        {analysisResult.dataType} Data
                      </Chip>
                      {analysisResult.intelligence?.threatLevel && (
                        <Chip 
                          size="sm" 
                          variant="flat" 
                          color={getThreatLevelColor(analysisResult.intelligence.threatLevel)}
                        >
                          Threat: {analysisResult.intelligence.threatLevel}
                        </Chip>
                      )}
                      {analysisResult.intelligence?.confidence && (
                        <Chip 
                          size="sm" 
                          variant="flat" 
                          color={getConfidenceColor(analysisResult.intelligence.confidence)}
                        >
                          Confidence: {analysisResult.intelligence.confidence}
                        </Chip>
                      )}
                      <Chip size="sm" variant="flat" color="default">
                        {new Date(analysisResult.processingTime).toLocaleString()}
                      </Chip>
                    </div>

                    <Divider />

                    {/* Intelligence Analysis */}
                    <div>
                      <h5 className="font-semibold mb-3">Intelligence Analysis</h5>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                        <div className="prose prose-sm max-w-none dark:prose-invert">
                          {formatAnalysisText(analysisResult.intelligence?.analysis)}
                        </div>
                      </div>
                    </div>

                    {/* Additional Intelligence Fields */}
                    {analysisResult.intelligence?.platformsAnalyzed && (
                      <div>
                        <h6 className="font-medium mb-2">Platforms Analyzed</h6>
                        <div className="flex flex-wrap gap-2">
                          {analysisResult.intelligence.platformsAnalyzed.map((platform, index) => (
                            <Chip key={index} size="sm" variant="flat" color="secondary">
                              {platform}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="border-t">
          <Button variant="light" onClick={handleModalClose}>
            Close
          </Button>
          {!analysisResult && (
            <Button 
              color="primary" 
              onClick={handleAnalyze}
              isLoading={loading}
              startContent={!loading && <CpuChipIcon className="h-4 w-4" />}
            >
              {loading ? 'Analyzing...' : 'Analyze with AI'}
            </Button>
          )}
          {analysisResult && (
            <Button 
              color="primary" 
              variant="light"
              onClick={resetModal}
            >
              New Analysis
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OSINTAnalysisModal; 